import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  styled,
  TextField,
} from '@mui/material';
import PageHeader from '../../controls/PageHeader';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import TextSearch from '../../controls/TextSearch';
import SearchIcon from '@mui/icons-material/Search';
import { useEffect, useState } from 'react';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import * as accountingSlice from '../../../data/accounting.slice';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { DataGridPro } from '../../tables/DataGridPro';
import {
  GridColDef,
  GridRowClassNameParams,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import { hasWordStartingWith } from '../../../utils/string.utils';
import { MoreVert } from '@mui/icons-material';
import { PricingModel } from '../../../data/models/pricing-model.model';
import PricingModelDialog, {
  PricingModelDialogAction,
} from './PricingModelDialog';
import * as lookupSlice from '../../../data/lookup.slice';
import ActivateDialog from '../ActivateDialog';
import ConfirmationDialog from '../../ConfirmationDialog';
import { CommonEntityState } from '../../../data/models/entity-state';
import CommonEntityStateChip from '../shared/CommonEntityStateChip';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';

const PREFIX = 'PricingModels';

const classes = {
  container: `${PREFIX}-container`,
  subHeader: `${PREFIX}-header`,
  contentContainer: `${PREFIX}-contentContainer`,
  search: `${PREFIX}-search`,
  statusSelect: `${PREFIX}-statusSelect`,
  addButton: `${PREFIX}-addButton`,
  moreButtonContainer: `${PREFIX}-moreButtonContainer`,
  moreButton: `${PREFIX}-moreButton`,
  hoveredRow: `${PREFIX}-hoveredRow`,
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  [`& .${classes.container}`]: {
    padding: theme.spacing(1),
  },
  [`& .${classes.contentContainer}`]: {
    padding: theme.spacing(2),
  },
  [`& .${classes.subHeader}`]: {
    justifyContent: 'space-between',
  },
  [`& .${classes.search}`]: {
    width: 320,
    marginRight: theme.spacing(3),
  },
  [`& .${classes.statusSelect}`]: {
    width: 200,
  },
  [`& .${classes.addButton}`]: {
    width: 144,
    height: 48,
  },
  [`& .${classes.moreButtonContainer}`]: {
    display: 'flex',
    justifyContent: 'end',
    width: '100%',
  },
  [`& .${classes.moreButton}`]: {
    marginRight: theme.spacing(1),
  },
}));

const StyledDataGridPro = styled(DataGridPro)(({ theme }) => ({
  cursor: 'pointer',
  [`& .${classes.hoveredRow}`]: {
    backgroundColor: theme.palette.action.hover,
  },
}));

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    minWidth: '170px',
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  ...theme.typography.body2,
}));

export default function PricingModels() {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();

  const isSuperReaderUser = useIsSuperReaderUser();

  const allStatuses = t('pricingmodel.all');
  const [searchText, setSearchText] = useState('');
  const [selectedStatus, setSelectedStatus] = useState(allStatuses);
  const [hoveredRow, setHoveredRow] = useState<string | undefined>(undefined);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [menuOpenedRow, setMenuOpenedRow] = useState<string | null>(null);
  const [pricingModelDialogOpen, setPricingModelDialogOpen] = useState(false);
  const [pricingModelDialogAction, setPricingDialogModelAction] = useState<
    PricingModelDialogAction | undefined
  >(undefined);
  const [selectedPricingModel, setSelectedPricingModel] =
    useState<PricingModel | null>(null);
  const [confirmDeletionDialogOpen, setConfirmDeletionDialogOpen] =
    useState(false);
  const [isDeactivate, setIsDeactivate] = useState(false);
  const [activateDialogTitle, setActivateDialogTitle] = useState('');
  const [activateDialogMessage, setActivateDialogMessage] = useState('');
  const [activateDialogPostiveFn, setActivateDialogPostiveFn] = useState<
    () => void
  >(() => null);
  const [activateDialogNegativeFn, setActivateDialogNegativeFn] = useState<
    () => void
  >(() => null);
  const [activateDialogOpen, setActivateDialogOpen] = useState(false);
  const allPricingModels = useAppSelector(
    accountingSlice.selectAllPricingModels,
  );

  const isEditable = (state?: CommonEntityState) =>
    state === CommonEntityState.Draft;

  const selectableStatuses = [
    CommonEntityState.Active,
    CommonEntityState.Inactive,
    CommonEntityState.Draft,
  ];

  const filteredPricingModels = allPricingModels.filter((pricingModel) =>
    hasWordStartingWith(pricingModel.name?.toLowerCase(), searchText),
  );

  useEffect(() => {
    dispatch(accountingSlice.fetchPricingFunctionsAsync());
    dispatch(lookupSlice.fetchLookupItemsAsync('chargeType'));
    dispatch(lookupSlice.fetchLookupItemsAsync('pricingCalculationGroup'));
  }, []);

  useEffect(() => {
    const states =
      selectedStatus && selectedStatus !== allStatuses
        ? [selectedStatus]
        : undefined;
    dispatch(accountingSlice.fetchPricingModelsAsync(states));
  }, [selectedStatus]);

  const statuses = selectableStatuses.map((status) => ({
    key: status as string,
    value: t(`common.${status}`),
  }));
  statuses.unshift({ key: allStatuses, value: allStatuses });

  const findPricingModel = (pricingModelUUID: string) => {
    return allPricingModels.find(
      (pricingModel) => pricingModel.pricingModelUUID === pricingModelUUID,
    );
  };

  const activatePricingModel = () => {
    const confirm = (start?: string, end?: string) => {
      if (selectedPricingModel) {
        updatePricingModelEffectiveDates(
          selectedPricingModel.pricingModelUUID!,
          start,
          end,
        );
      }

      setActivateDialogOpen(false);
    };
    const cancel = () => {
      setActivateDialogOpen(false);
    };
    setIsDeactivate(false);
    setActivateDialogTitle(t('pricingmodel.confirmActivatePricingModelTitle'));
    setActivateDialogMessage(
      t('pricingmodel.confirmActivatePricingModelMessage'),
    );
    setActivateDialogPostiveFn(() => confirm);
    setActivateDialogNegativeFn(() => cancel);
    setActivateDialogOpen(true);
  };

  const deactivatePricingModel = () => {
    const confirm = (start?: string, end?: string) => {
      if (selectedPricingModel) {
        updatePricingModelEffectiveDates(
          selectedPricingModel.pricingModelUUID!,
          start,
          end,
        );
      }

      setActivateDialogOpen(false);
    };
    const cancel = () => {
      setActivateDialogOpen(false);
    };
    setIsDeactivate(true);
    setActivateDialogTitle(
      t('pricingmodel.confirmDeactivatePricingModelTitle'),
    );
    setActivateDialogMessage(
      t('pricingmodel.confirmDeactivatePricingModelMessage'),
    );
    setActivateDialogPostiveFn(() => confirm);
    setActivateDialogNegativeFn(() => cancel);
    setActivateDialogOpen(true);
  };

  const updatePricingModelEffectiveDates = (
    pricingModelUUID: string,
    start?: string,
    end?: string,
  ) => {
    dispatch(
      accountingSlice.updatePricingModelEffectiveDatesAsync(
        pricingModelUUID,
        start,
        end,
      ),
    );
  };

  const closeMenu = () => {
    setMenuAnchorEl(null);
    setHoveredRow(undefined);
    setMenuOpenedRow(null);
  };

  const handleSearchTextChange = (val: string) => {
    setSearchText(val);
  };

  const handleStatusChange = (status: string) => {
    setSelectedStatus(status);
  };

  const handleAddPricingModelClick = () => {
    setSelectedPricingModel(null);
    setPricingDialogModelAction(PricingModelDialogAction.Add);
    setPricingModelDialogOpen(true);
  };

  const handlePricingModelDialogClose = () => {
    setPricingModelDialogOpen(false);
    setSelectedPricingModel(null);
  };

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    pricingModelUUID: string,
  ) => {
    event.stopPropagation();

    setMenuOpenedRow(pricingModelUUID);
    setMenuAnchorEl(event.currentTarget);
    const selectedPricingModel = findPricingModel(pricingModelUUID);
    setSelectedPricingModel(selectedPricingModel ?? null);
  };

  const handleMenuClose = () => {
    closeMenu();
    setSelectedPricingModel(null);
  };

  const handleEditClick = () => {
    closeMenu();
    if (selectedPricingModel) {
      setPricingDialogModelAction(PricingModelDialogAction.Edit);
      setPricingModelDialogOpen(true);
    }
  };

  const handleSetActiveClick = () => {
    closeMenu();
    activatePricingModel();
  };

  const handleSetInactiveClick = () => {
    closeMenu();
    deactivatePricingModel();
  };

  const handleDeleteClick = () => {
    closeMenu();
    setConfirmDeletionDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedPricingModel) {
      dispatch(accountingSlice.deletePricingModelAsync(selectedPricingModel));
    }

    setConfirmDeletionDialogOpen(false);
  };

  const handleDuplicateClick = () => {
    closeMenu();
    if (selectedPricingModel) {
      setPricingDialogModelAction(PricingModelDialogAction.Duplicate);
      setPricingModelDialogOpen(true);
    }
  };

  const handleRowClick = (params: GridRowParams) => {
    const pricingModelUUID = params.row.pricingModelUUID;
    const selectedPricingModel = findPricingModel(pricingModelUUID);
    if (selectedPricingModel) {
      setSelectedPricingModel(selectedPricingModel);
      if (isEditable(params.row.state)) {
        setPricingDialogModelAction(PricingModelDialogAction.Edit);
      } else {
        setPricingDialogModelAction(PricingModelDialogAction.View);
      }

      setPricingModelDialogOpen(true);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('pricingmodel.name'),
      sortable: false,
      minWidth: 400,
    },
    {
      field: 'state',
      headerName: t('pricingmodel.status'),
      sortable: false,
      minWidth: 87,
      renderCell: (params) => (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="100%">
          <CommonEntityStateChip state={params.row.state} />
        </Box>
      ),
    },
    {
      field: 'action',
      headerName: '',
      sortable: false,
      flex: 1,
      renderCell: (params) => {
        if (isSuperReaderUser) {
          return null;
        }

        if (params.row.pricingModelUUID === hoveredRow) {
          return (
            <Box className={classes.moreButtonContainer}>
              <IconButton
                className={classes.moreButton}
                onClick={(event) =>
                  handleMenuClick(event, params.row.pricingModelUUID)
                }>
                <MoreVert />
              </IconButton>
            </Box>
          );
        }
      },
    },
  ];

  return (
    <StyledPaper>
      <Box className={classes.container}>
        <PageHeader
          title={t('pricingmodel.title')}
          returnLink="/settings"
          returnLinkText={t('settings.title')}
        />
        <Stack direction="column" className={classes.contentContainer}>
          <Stack direction="row" className={classes.subHeader}>
            <Stack direction="row">
              <TextSearch
                id="text-search"
                className={classes.search}
                icon={<SearchIcon />}
                label={t('pricingmodel.search')}
                currentSearchFilter={searchText}
                onSearchChange={handleSearchTextChange}
              />
              <TextField
                select
                className={classes.statusSelect}
                label={t('pricingmodel.status')}
                variant="outlined"
                value={selectedStatus}
                onChange={(evt) => handleStatusChange(evt.target.value)}>
                {statuses?.map((status) => {
                  return (
                    <MenuItem value={status.key} key={status.key}>
                      {status.value}
                    </MenuItem>
                  );
                })}
              </TextField>
            </Stack>
            {!isSuperReaderUser && (
              <Button
                className={classes.addButton}
                color="primary"
                variant="contained"
                size="large"
                onClick={handleAddPricingModelClick}>
                {t('common.add')}
              </Button>
            )}
          </Stack>
          <StyledDataGridPro
            disableColumnMenu={true}
            getRowId={(row) => row.pricingModelUUID}
            rows={filteredPricingModels ?? []}
            columns={columns}
            rowCount={filteredPricingModels?.length}
            pagination={true}
            autoHeight
            onRowClick={handleRowClick}
            getRowClassName={(params) =>
              params.row.pricingModelUUID === menuOpenedRow
                ? classes.hoveredRow
                : ''
            }
            slotProps={{
              row: {
                onMouseEnter: (event) => {
                  const id = event.currentTarget.dataset.id;
                  setHoveredRow(id);
                },
              },
            }}
          />
        </Stack>
      </Box>
      <StyledMenu
        anchorEl={menuAnchorEl}
        open={!!menuAnchorEl}
        onClose={handleMenuClose}
        transformOrigin={{
          vertical: 'top',
          horizontal: 120,
        }}>
        {isEditable(selectedPricingModel?.state as CommonEntityState) && (
          <StyledMenuItem onClick={() => handleEditClick()}>
            {t('pricingmodel.edit')}
          </StyledMenuItem>
        )}
        {(selectedPricingModel?.state === CommonEntityState.Draft ||
          selectedPricingModel?.state === CommonEntityState.Inactive) && (
          <StyledMenuItem onClick={() => handleSetActiveClick()}>
            {t('pricingmodel.setActive')}
          </StyledMenuItem>
        )}
        {selectedPricingModel?.state === CommonEntityState.Active && (
          <StyledMenuItem onClick={() => handleSetInactiveClick()}>
            {t('pricingmodel.setInactive')}
          </StyledMenuItem>
        )}
        <StyledMenuItem onClick={() => handleDuplicateClick()}>
          {t('pricingmodel.duplicate')}
        </StyledMenuItem>
        {(selectedPricingModel?.state === CommonEntityState.Draft ||
          selectedPricingModel?.state === CommonEntityState.Inactive) && (
          <StyledMenuItem onClick={() => handleDeleteClick()}>
            {t('pricingmodel.delete')}
          </StyledMenuItem>
        )}
      </StyledMenu>
      <PricingModelDialog
        pricingModel={selectedPricingModel ?? undefined}
        open={pricingModelDialogOpen}
        action={pricingModelDialogAction}
        close={handlePricingModelDialogClose}
      />
      <ConfirmationDialog
        title={t('pricingmodel.confirmDeletePricingModelTitle')}
        message={t('pricingmodel.confirmDeletePricingModelMessage')}
        open={confirmDeletionDialogOpen}
        positiveAction={handleDeleteConfirm}
        negativeAction={() => setConfirmDeletionDialogOpen(false)}
      />
      <ActivateDialog
        title={activateDialogTitle}
        message={activateDialogMessage}
        open={activateDialogOpen}
        forActivate={!isDeactivate}
        handleOk={activateDialogPostiveFn}
        handleCancel={activateDialogNegativeFn}
      />
    </StyledPaper>
  );
}
